import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { formatRelativeShort } from "@/design/format";
import {
  Tabs,
  DataTable,
  type Column,
  Pill,
  Avatar,
  Button,
  EmptyState,
  Stat,
} from "@/design/components";
import { Camera } from "lucide-react";
import { AssuranceBadge, HeimdallBadge, AnalystReviewingIndicator, PaymentMethodBadge } from "@/design/components";
import { useSeed, usePeopleIndex, useOrgIndex, useMoney, useAssurance, useAiReviewFor, useAnalystName, useProjects } from "@/domain/hooks";
import type { SpendRequest } from "@/domain/types";
import { api, demoModeActive } from "@/domain/api";
import { isAboveStratusThreshold } from "@/domain/spendThreshold";
import { SpendAskDrawer } from "./SpendAskDrawer";
import { EvidenceModal } from "./EvidenceModal";

export function ApprovalQueue({
  audience,
  scope,
  canApprove = true,
}: {
  audience: string;
  scope?: { projectId?: string; subProjectId?: string };
  canApprove?: boolean;
}) {
  const seed = useSeed();
  const people = usePeopleIndex();
  const orgs = useOrgIndex();
  const money = useMoney();
  const assuranceFor = useAssurance();
  const reviewFor = useAiReviewFor();
  const analystName = useAnalystName();
  // Demo mode raises and mutates asks in the seed store, so the queue must read the seed
  // too — otherwise a demo-raised ask never reaches the approver. Live production reads the
  // DB (getApproverQueue is scoped server-side to what this approver may action).
  const demo = demoModeActive();
  const [tab, setTab] = useState("my");
  const [open, setOpen] = useState<SpendRequest | null>(null);
  const [evidenceAsk, setEvidenceAsk] = useState<SpendRequest | null>(null);
  const [dbRequests, setDbRequests] = useState<SpendRequest[]>([]);
  const [loading, setLoading] = useState(!demo);
  const [loadError, setLoadError] = useState<string | null>(null);
  const evidenceCount = (id: string) => (seed.evidence ?? []).filter((e) => e.linkedAskId === id).length;
  // Real, API-backed project ids (falls back to the demo seed's projects
  // only when real data isn't available) - used below instead of reading
  // seed.projects directly, since seed is always the demo/fixture blob
  // (hydrated regardless of mode) and its project ids are fake slugs a
  // real getApproverQueue(id) call would always reject.
  const realProjects = useProjects();
  // Keyed on the ids (a plain string), never the array object: this effect
  // setStates, so depending on the array identity would re-fire it on its own
  // re-render — an unbounded loop of approver-queue requests that hangs the page.
  const projectIds = useMemo(
    () => (scope?.projectId ? [scope.projectId] : realProjects.map((project) => project.id)),
    [scope?.projectId, realProjects],
  );
  const projectIdsKey = projectIds.join(",");

  useEffect(() => {
    if (demo) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    Promise.allSettled(projectIds.map((projectId) => api.getApproverQueue(projectId)))
      .then((results) => {
        if (cancelled) return;
        const fulfilled = results.filter((result): result is PromiseFulfilledResult<Record<string, unknown>[]> => result.status === "fulfilled");
        const rows = fulfilled.flatMap((result) => result.value).map(normalizeSpendRequest);
        const failed = results.length > 0 && fulfilled.length === 0;
        setDbRequests(rows);
        setLoadError(failed ? "Unable to load the approval queue from the database." : null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- projectIdsKey stands in for projectIds
  }, [demo, projectIdsKey]);

  const inScope = (r: SpendRequest) => {
    if (scope?.projectId && r.projectId !== scope.projectId) return false;
    if (scope?.subProjectId && r.subProjectId !== scope.subProjectId) return false;
    return true;
  };

  // Newest first, applied once here so every tab (My queue / All queues / Confirmations
  const all = (demo ? seed.spendRequests : dbRequests)
    .filter(inScope)
    .sort((a, b) => (a.submittedAt < b.submittedAt ? 1 : a.submittedAt > b.submittedAt ? -1 : 0));
  // Above-threshold asks that Stratus has not yet marked verified belong only in the
  // Stratus "above threshold" queue (spec §6.3) — they surface here for the contractor
  // lead to approve once Stratus has verified them (verifiedAt set).
  const pending = all.filter(
    (r) => (r.state === "proposed" || r.state === "flagged") && !(isAboveStratusThreshold(r) && !r.verifiedAt),
  );
  const confirmations = all.filter((r) => r.state === "evidence_submitted");
  const rows = tab === "confirmations" ? confirmations : tab === "all" ? all : pending;

  const totalPending = pending.reduce((s, r) => s + r.amount, 0);
  const approvedToday = all.filter((r) => r.state === "approved_for_spend").length;

  const columns: Column<SpendRequest>[] = [
    {
      key: "reporter",
      header: "Reporter",
      cell: (r) => (
        <div className="flex items-center gap-2">
          <Avatar initials={people[r.reporterId]?.avatarInitials ?? "?"} size={28} />
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-ink">{people[r.reporterId]?.fullName}</p>
            <p className="truncate text-xs text-faint">{orgs[r.organisationId]?.name}</p>
          </div>
        </div>
      ),
    },
    {
      key: "project",
      header: "Project / Milestone",
      cell: (r) => (
        <div>
          <p className="text-sm text-ink">{seed.projects.find((p) => p.id === r.projectId)?.name}</p>
          <p className="text-xs text-faint">{seed.milestones.find((m) => m.id === r.milestoneId)?.name ?? "–"}</p>
        </div>
      ),
    },
    {
      key: "amount",
      header: "Amount",
      align: "right",
      cell: (r) => (
        <div className="flex items-center justify-end gap-2">
          <span className="whitespace-nowrap tabular-nums text-ink">{money(r.amount)}</span>
          {r.thresholdFlag && <Pill variant="warn">Threshold · 1</Pill>}
        </div>
      ),
    },
    {
      key: "purpose",
      header: "Purpose & supplier",
      cell: (r) => (
        <div className="max-w-xs">
          <p className="line-clamp-1 text-sm text-mute">{r.title}</p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className="text-xs text-faint">{r.supplier ?? "–"}</span>
            <AssuranceBadge level={assuranceFor(r)} />
            {/* TODO(heimdall): AI analyst not built yet — re-enable the per-row review badge when Heimdall ships.
            {(() => {
              const rev = reviewFor(r);
              return rev.state === "pending"
                ? <AnalystReviewingIndicator name={analystName} />
                : <HeimdallBadge state={rev.state} count={rev.findingIds.length} name={analystName} />;
            })()}
            */}
            <PaymentMethodBadge method={r.paymentMethod} />
          </div>
        </div>
      ),
    },
    {
      key: "age",
      header: "In queue",
      cell: (r) => {
        const hours = (Date.now() - new Date(r.submittedAt).getTime()) / 3.6e6;
        const tone = hours <= 48 ? "text-mute" : hours <= 168 ? "text-warn" : "text-bad";
        return <span className={`data ${tone}`}>{formatRelativeShort(r.submittedAt)}</span>;
      },
    },
    {
      key: "evidence",
      header: "Proof",
      align: "center",
      cell: (r) => {
        const n = evidenceCount(r.id);
        return (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setEvidenceAsk(r); }}
            className="focus-ring inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-line px-2 py-1 text-xs text-mute transition-colors hover:bg-subtle hover:text-ink"
            title="View evidence"
          >
            <Camera className="h-3.5 w-3.5" /> {n > 0 ? <span className="tabular-nums">{n}</span> : "View"}
          </button>
        );
      },
    },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (r) => (
        <div className="flex justify-end">
          <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); setOpen(r); }}>Review</Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-base font-semibold text-ink">Approval queue · {audience}</h2>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Pending approvals" value={String(pending.length)} sub={pending.length ? "awaiting review" : "all clear"} subTone={pending.length ? "warn" : "ok"} hero={pending.length > 0} />
        <Stat label="Total value pending" value={money(totalPending)} sub="in proposed state" />
        <Stat label="Approved today" value={String(approvedToday)} sub="moved to committed" subTone={approvedToday > 0 ? "ok" : "neutral"} />
        <Stat label="Avg time in queue" value="22 hours" sub="median across open asks" subTone="warn" />
      </div>

      {totalPending > 350_000_000 && (
        <div className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-warn/30 bg-warnSoft px-4 py-2.5">
          <p className="text-sm text-warn">
            Approving the current queue will bring this programme to 380M UGX observed spend. The next ask above 400M UGX will require a Clause 21 compliance declaration.
          </p>
          <Link to="/stratus/controls/compliance" className="shrink-0 whitespace-nowrap text-xs font-medium text-warn underline hover:text-warn/80">
            Open compliance register
          </Link>
        </div>
      )}

      <Tabs
        variant="pill"
        items={[
          { value: "my", label: "My queue", count: pending.length },
          { value: "all", label: "All queues", count: all.length },
          { value: "confirmations", label: "Confirmations", count: confirmations.length },
        ]}
        value={tab}
        onValueChange={setTab}
      />

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        onRowClick={setOpen}
        empty={loading ? <EmptyState title="Loading approval queue" body="Fetching current spend requests from the database…" /> : loadError ? <EmptyState title="Queue unavailable" body={loadError} /> :
          tab === "confirmations"
            ? <EmptyState title="No confirmations waiting" body="Evidence-submitted asks land here for oversight to confirm spend is matched and verified." />
            : tab === "all"
            ? <EmptyState title="No spend asks" body="Nothing in scope for this programme yet." />
            : <EmptyState title="Your queue is clear" body="Reporters submit asks from the field PWA; new asks appear here within a minute of submission." />
        }
      />

      <SpendAskDrawer ask={open} open={!!open} onOpenChange={(o) => !o && setOpen(null)} canApprove={canApprove} />
      <EvidenceModal ask={evidenceAsk} open={!!evidenceAsk} onOpenChange={(o) => !o && setEvidenceAsk(null)} />
    </div>
  );
}

function normalizeSpendRequest(row: Record<string, unknown>): SpendRequest {
  return {
    id: String(row.id),
    reporterId: String(row.requesterid ?? row.requesterId ?? ""),
    organisationId: String(row.requesterorganisationid ?? row.organisationid ?? row.organisationId ?? ""),
    projectId: String(row.projectid ?? row.projectId ?? ""),
    subProjectId: row.subprojectid == null ? undefined : String(row.subprojectid),
    milestoneId: row.milestoneid == null ? undefined : String(row.milestoneid),
    title: String(row.title ?? ""),
    amount: Number(row.amount ?? 0),
    currency: (String(row.currency ?? "UGX") as SpendRequest["currency"]),
    supplier: row.targetsupplier == null ? undefined : String(row.targetsupplier),
    purpose: String(row.purpose ?? ""),
    documents: [],
    state: String(row.state ?? "proposed") as SpendRequest["state"],
    submittedAt: String(row.createdat ?? row.submittedAt ?? new Date().toISOString()),
  };
}
